import { CloseOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Collapse,
  Descriptions,
  Form,
  Input,
  Radio,
  Select,
  Tag,
} from "antd";
import React from "react";
import { AddButton } from "~/components/buttons/AddButton.jsx";
import { $t } from "~/i18n.js";
import { DateFormat } from "../date/DateFormat.jsx";


export const ExtrasFormitem = ({
  id = "",
  value = [],
  onChange = (e) => {},
}) => {
  const types = [
    { value: "text", label: $t("Text") },
    { value: "number", label: $t("Nummer") },
    { value: "date", label: $t("Datum") },
    { value: "time", label: $t("Tijd") },
    { value: "checkbox", label: $t("Meerdere opties") },
    { value: "radio", label: $t("Enkele optie") },
  ];

  // Handle cell value change
  const handleCellChange = (rowIndex, newData) => {
    const newRows = value.map((row, idx) =>
      idx === rowIndex ? { ...row, ...newData } : row
    );
    onChange(newRows);
  };
  
  return (
    <div>
      {value && value.length > 0 && (
        <Collapse size="small">
          {value?.map((row, idx) => {
            return (
              <Collapse.Panel
                key={row.key || idx}
                header={row.name || $t("Nieuwe vraag")}
                extra={
                  <Button
                    size="small"
                    danger
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      const newRows = value.filter((_, i) => i !== idx);
                      onChange(newRows);
                    }}
                  />
                }
              >
                <div className="row" style={{ marginBottom: 8, gap: 8 }}>
                  <Input
                    style={{ width: 150 }}
                    value={row.field}
                    variant={row.field ? "filled" : 'outlined'}
                    status={!row.field ? "error" : ""}
                    onChange={(e) => {
                      // Clean the key: remove spaces and non-alphanumeric characters
                      const cleanKey = e.target.value.replace(
                        /[^a-zA-Z0-9_]/g,
                        ""
                      );
                      handleCellChange(idx, { field: cleanKey });
                    }}
                    size="small"
                    placeholder={$t("Veldnaam (key)")}
                  />
                  <Input
                    value={row.name}
                    onChange={(e) =>
                      handleCellChange(idx, { name: e.target.value })
                    }
                    size="small"
                    placeholder={$t("Vraag (label)")}
                  />
                </div>
                <Input.TextArea
                  value={row.extra}
                  onChange={(e) =>
                    handleCellChange(idx, { extra: e.target.value })
                  }
                  size="small"
                  rows={2}
                  placeholder={$t("Beschrijving van veld")}
                  style={{ marginBottom: 8 }}
                />

                <div className="row" style={{ gap: 8 }}>
                  <Select
                    value={row.input_type}
                    onChange={(value) => {
                      handleCellChange(idx, { input_type: value });
                    }}
                    size="small"
                    style={{ width: 150 }}
                    options={types}
                    placeholder={$t("Type")}
                  />
                  <Checkbox
                    checked={!!row.required}
                    onChange={(e) =>
                      handleCellChange(idx, { required: e.target.checked })
                    }
                    style={{ marginRight: 6 }}
                  >
                    {$t("Verplicht")}
                  </Checkbox>
                  {(row.input_type === "checkbox" ||
                    row.input_type === "radio") && (
                    <Select
                      mode="tags"
                      tokenSeparators={[","]}
                      size="small"
                      value={row.options}
                      onChange={(e) => handleCellChange(idx, { options: e })}
                      placeholder={$t("Opties")}
                    />
                  )}
                </div>
              </Collapse.Panel>
            );
          })}
        </Collapse>
      )}
      <div style={{ marginTop: 6, marginLeft: 6 }}>
        <AddButton
          onClick={() => {
            const newRow = {
              field: "",
              name: "",
              extra: "",
              input_type: "text",
              options: [],
              required: false,
            };
            onChange([...(value || []), newRow]);
          }}
        />
      </div>
    </div>
  );
};

export const ExtrasForm = ({ extras }) => {
  return extras.map((extra, idx) => {
    let inputNode = null;

    switch (extra.input_type) {
      case "text":
        inputNode = <Input />;
        break;
      case "number":
        inputNode = <Input type="number" />;
        break;
      case "date":
        inputNode = <Input type="date" />;
        break;
      case "time":
        inputNode = <Input type="time" />;
        break;
      case "checkbox":
        inputNode = (
          <Checkbox.Group
            options={extra.options.map((opt) => ({ label: opt, value: opt }))}
          />
        );
        break;
      case "radio":
        inputNode = (
          <Radio.Group
            options={extra.options.map((opt) => ({ label: opt, value: opt }))}
          />
        );
        break;
      default:
        inputNode = <Input />;
    }
    return (
      <Form.Item
        key={idx}
        label={extra.name}
        name={["extra", extra.field || extra.name]}
        extra={extra.extra}
        rules={[
          {
            required: extra.required,
            message: $t("Verplicht"),
          },
        ]}
      >
        {inputNode}
      </Form.Item>
    );
  });
};

export const ExtrasDescription = ({ extras, extraData }) => {
  const formatValue = (extra) => {
    const value = extraData?.[extra.field];
    if (value === undefined || value === null) return "";
    switch (extra.input_type) {
      case "checkbox":
        if (Array.isArray(value)) {
          return value.map((v, i) => <Tag key={i}>{v}</Tag>);
        }
        return value ? <Tag>{String(value)}</Tag> : "";
      case "radio":
        return value ? <Tag>{String(value)}</Tag> : "";
      case "date": {
        return <DateFormat date={value} />;
      }
      case "time": {
        return String(value);
      }
      default:
        return String(value);
    }
  };
  return (
    <Descriptions
      column={1}
      size="small"
      bordered={false}
      style={{ marginTop: 6 }}
    >
      {extras.map((extra, idx) => (
        <Descriptions.Item key={idx} label={extra.name}>
          {formatValue(extra) || "-"}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
};
